import { useState, useMemo, useCallback } from 'react';
import { DataTable } from '@/components/common';
import { getPromoOfferColumns } from './PromoOfferTable.columns';
import type { PromoOffer } from '../../types';

interface PromoOfferTableProps {
  data: PromoOffer[];
  isLoading?: boolean;
  error?: Error | null;
  renderEditForm?: (
    promoOffer: PromoOffer,
    onClose: () => void
  ) => React.ReactNode;
  onViewDetails?: (promoOffer: PromoOffer) => void;
}

export function PromoOfferTable({
  data,
  isLoading = false,
  error = null,
  renderEditForm,
  onViewDetails,
}: PromoOfferTableProps) {
  const [editingPromoOffer, setEditingPromoOffer] = useState<PromoOffer | null>(null);
  
  const handleEdit = useCallback((promoOffer: PromoOffer) => {
    setEditingPromoOffer(promoOffer);
  }, []);
  
  const columns = useMemo(
    () => getPromoOfferColumns({ 
      onEdit: renderEditForm ? handleEdit : undefined,
      onViewDetails 
    }), 
    [handleEdit, renderEditForm, onViewDetails]
  );

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading promo offers...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load promo offers</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">No promo offers found</p>
          <p className="text-sm text-muted-foreground">
            Create your first promo offer to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <DataTable
      data={data}
      columns={columns}
      editingItem={editingPromoOffer}
      onEditingItemChange={setEditingPromoOffer}
      renderEditForm={renderEditForm}
      config={{
        enableColumnVisibility: true,
        enableSorting: true,
        enableFiltering: true,
        defaultPageSize: 10,
        pageSizeOptions: [5, 10, 20, 50],
        editDrawerTitle: 'Edit Promo Offer',
        editDrawerDescription: 'Update promo offer information',
        searchPlaceholder: 'Search promo offers, brands, descriptions...',
      }}
      testIdPrefix="promo-offers-table"
    />
  );
}